import { type IntranetContentDetail } from '@gb-ticket/shared-types';
import { CheckIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { useAcknowledgeIntranetContent } from '@/products/intranet/features/intranet/use-intranet-ack';

/** Warning until the reader confirms; success afterwards. Hidden when no confirmation is required. */
export function AckBanner({ content }: { content: IntranetContentDetail }) {
  const copy = useVoiceCopy();
  const acknowledge = useAcknowledgeIntranetContent(content.slug);

  if (!content.requiresAcknowledgement || content.isDraft) {
    return null;
  }

  if (content.acknowledgedAt) {
    return (
      <p
        role="status"
        className="rounded-lg border border-status-success-border bg-status-success px-4 py-3 text-sm text-status-success-foreground"
      >
        <CheckIcon className="mr-2 inline size-4 align-text-bottom" aria-hidden />
        {copy('intranet.ack.confirmed', { at: formatDateTime(content.acknowledgedAt) })}
      </p>
    );
  }

  async function confirm() {
    try {
      await acknowledge.mutateAsync();
      toast.success('Lesebestätigung gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Bestätigen fehlgeschlagen.');
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-status-warning-border bg-status-warning px-4 py-3 text-status-warning-foreground sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm">
        {copy('intranet.ack.please-confirm')}
        {content.ackDeadline ? ` (Frist: ${formatDateTime(content.ackDeadline)})` : ''}.
      </p>
      <Button
        type="button"
        size="sm"
        className="shrink-0 bg-warning text-warning-foreground hover:bg-warning/90"
        disabled={acknowledge.isPending}
        onClick={() => void confirm()}
      >
        Ich habe das gelesen
      </Button>
    </div>
  );
}
